import React, { useMemo, useState, useEffect } from "react";
import { SectionEntity } from "@/database";
import { Folder, FolderOpen, FileText, Play } from "lucide-react";
import { Button } from "../..";

interface DenmarkFavSectionTreeProps {
  sections: SectionEntity[];
  selectedSectionIds?: Array<number | string>;
  onSectionClick: (section: SectionEntity) => void;
  getDenmarkChildSectionList: (params: any) => void;
  isSectionLoading: boolean;
  showPriorityPanel?: boolean;
  onPrioritySectionSelect?: (section: SectionEntity) => void;
  selectedHost?: string;
  disablePlayButton?: boolean;
}

const DenmarkFavSectionTree: React.FC<DenmarkFavSectionTreeProps> = ({
  sections,
  selectedSectionIds = [],
  onSectionClick,
  getDenmarkChildSectionList,
  isSectionLoading,
  showPriorityPanel,
  onPrioritySectionSelect,
  selectedHost,
  disablePlayButton = false,
}) => {
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({});
  const [hoveredSection, setHoveredSection] = useState<string | null>(null);
  const [fetchedSectionIds, setFetchedSectionIds] = useState<Set<string>>(
    () => new Set()
  );
  const [loadingSectionKey, setLoadingSectionKey] = useState<string | null>(
    null
  );

  useEffect(() => {
    if (!isSectionLoading && loadingSectionKey !== null) {
      setLoadingSectionKey(null);
    }
  }, [isSectionLoading, loadingSectionKey]);

  const selectedSet = useMemo(
    () => new Set(selectedSectionIds.map((id) => String(id))),
    [selectedSectionIds]
  );

  const favSections = useMemo(() => {
    if (!Array.isArray(sections) || sections.length === 0) return [];
    return sections;
  }, [sections]);

  // Use favouriteId as the unique render/state key; fall back to msId.
  const getFavKey = (section: SectionEntity): string =>
    String((section as any).favouriteId ?? section.msId ?? section["@id"]);

  const toggleSection = (key: string) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const renderSection = (
    section: SectionEntity,
    level: number,
    parentRenderKey: string = ""
  ) => {
    const favKey = getFavKey(section);
    // Unique key for rendering — prefixed with parent path so duplicate msIds never collide
    const renderKey = parentRenderKey ? `${parentRenderKey}/${favKey}` : favKey;
    // Selection still uses msId so it matches what the parent passes in selectedSectionIds
    const selectionKey = section.msId != null ? String(section.msId) : favKey;
    const children = section.childSections ?? [];
    const canExpand = children.length > 0;
    const isExpanded = !!expandedSections[renderKey];
    const isSelected = selectedSet.has(selectionKey);
    const isHovered = hoveredSection === renderKey;
    const isTopParent = level === 0;
    const isLoadingThis = isSectionLoading && loadingSectionKey === renderKey;

    return (
      <div key={renderKey}>
        <div
          role="button"
          tabIndex={0}
          className="btn w-100 d-flex align-items-center text-start"
          style={{
            minHeight: "30px",
            padding: "5px 8px",
            marginBottom: "1px",
            paddingLeft: `${level * 22}px`,
            backgroundColor: isSelected
              ? "#dfe6eb"
              : isHovered
                ? "#f3f6f8"
                : "transparent",
            color: "#565b60",
            fontSize: "18px",
            fontWeight: 500,
            textTransform: "none",
            transition:
              "background-color 0.2s ease, transform 0.15s ease, box-shadow 0.15s ease",
            transform: isHovered ? "translateY(-2px)" : "translateY(0)",
            boxShadow: isHovered ? "0 4px 10px rgba(0,0,0,0.1)" : "none",
            borderRadius: isHovered ? "6px" : "0",
            zIndex: isHovered ? 1 : "auto",
            position: "relative",
          }}
          onMouseEnter={() => setHoveredSection(renderKey)}
          onMouseLeave={() =>
            setHoveredSection((prev) => (prev === renderKey ? null : prev))
          }
          onClick={() => {
            if (canExpand) toggleSection(renderKey);
          }}
          onKeyDown={(event) => {
            if ((event.key === "Enter" || event.key === " ") && canExpand) {
              event.preventDefault();
              toggleSection(renderKey);
            }
          }}
        >
          <span
            className="me-2 d-flex align-items-center justify-content-center"
            style={{ width: "28px", minWidth: "28px" }}
          >
            {isTopParent ? (
              isExpanded ? (
                <FolderOpen size={22} color="#555f6b" />
              ) : (
                <Folder size={22} color="#555f6b" />
              )
            ) : (
              <FileText size={18} color="#555f6b" />
            )}
          </span>
          <span
            className="text-truncate"
            onClick={(event) => {
              event.stopPropagation();
              const fetchId = String(section.msId ?? renderKey);
              const alreadyFetched = fetchedSectionIds.has(fetchId);
              if (!alreadyFetched) {
                getDenmarkChildSectionList({
                  parentId: section.msId,
                  parentsParentId: section.parents?.[0]?.parentId,
                  hostId: Number(section.parents[0].hostId),
                  startIndex: 0,
                  recordsPerPage: 500,
                  listActivationStatus: [0, 1, 2, 3, 4, 5, 6, 7, 9],
                });
                setFetchedSectionIds((prev) => new Set(prev).add(fetchId));
                setLoadingSectionKey(renderKey);
              }
              toggleSection(renderKey);
            }}
          >
            {section.name ?? "Untitled section"}
          </span>
          <span className="ms-auto d-flex align-items-center">
            {isLoadingThis ? (
              <span
                className="spinner-border spinner-border-sm text-primary"
                role="status"
                style={{ width: "18px", height: "18px" }}
              />
            ) : (
              <Button
                type="outline-primary"
                HTMLButtonType="button"
                icon={
                  <Play
                    size={11}
                    fill={disablePlayButton ? "#adb5bd" : "#1a87d4"}
                    color={disablePlayButton ? "#adb5bd" : "#1a87d4"}
                  />
                }
                className="btn-sm p-0 d-flex align-items-center justify-content-center no-fill-hover"
                style={{
                  width: "20px",
                  height: "20px",
                  minWidth: "20px",
                  opacity: isHovered ? 1 : 0,
                  visibility: isHovered ? "visible" : "hidden",
                  pointerEvents:
                    isHovered && !disablePlayButton ? "auto" : "none",
                  transition: "opacity 0.15s ease, visibility 0.15s ease",
                  cursor: disablePlayButton ? "not-allowed" : "pointer",
                  borderColor: disablePlayButton ? "#adb5bd" : undefined,
                }}
                isDisabled={disablePlayButton}
                onClick={(event) => {
                  event.stopPropagation();
                  if (disablePlayButton) return;
                  if (showPriorityPanel) {
                    onPrioritySectionSelect?.(section);
                  } else {
                    onSectionClick(section);
                  }
                }}
              />
            )}
          </span>
        </div>

        {canExpand && isExpanded && (
          <div>
            {children.map((child) =>
              renderSection(child, level + 1, renderKey)
            )}
          </div>
        )}
      </div>
    );
  };

  if (favSections.length === 0) {
    if (isSectionLoading && loadingSectionKey === null) {
      return (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ padding: "8px 8px" }}
        >
          <span
            className="spinner-border spinner-border-sm text-primary"
            role="status"
          />
        </div>
      );
    }
    return (
      <div
        className="d-flex align-items-center justify-content-center text-muted"
        style={{ minHeight: "500px", fontSize: "0.95rem" }}
      >
        No favourites available
      </div>
    );
  }

  return (
    <div style={{ maxHeight: "500px", overflowY: "auto" }}>
      {favSections.map((section) => renderSection(section, 0))}
    </div>
  );
};

export default DenmarkFavSectionTree;
